import React, { useCallback, useEffect, useRef } from 'react';
import { useApolloClient } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import Table from '@mui/material/Table';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import TableBody from '@mui/material/TableBody';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { TableCell } from 'common/components/Table';
import {
    IBasicUserFragment,
    IContentTypeValue,
    IPortfolioItemChildFragment,
} from 'graphql/types';
import { useGlobalDrawer } from 'common/hooks/useGlobalDrawer';
import { useRouteQuery } from 'route/hooks/useRouteQuery';
import { IGlobalDrawerType } from 'common/types';
import { ReceivedCertificatePortfolioItemTableRow } from 'skill/components/ReceivedCertificatePortfolioItemTable/ReceivedCertificatePortfolioItemTableRow';
import { TSkillOption } from 'skill/types';

interface IProps {
    certificatePortfolioItems: IPortfolioItemChildFragment[];
    portfolioItemId: string;
    user?: IBasicUserFragment;
    skill?: TSkillOption | null;
    hideUserBar?: boolean;
    onUpdate(): void;
}

export const ReceivedCertificatePortfolioItemTable = ({
    certificatePortfolioItems,
    portfolioItemId,
    user,
    skill,
    hideUserBar,
    onUpdate,
}: IProps) => {
    const [translate] = useTranslation();
    const { openGlobalDrawer, closeGlobalDrawer } = useGlobalDrawer();
    const client = useApolloClient();
    const navigate = useNavigate();
    const location = useLocation();
    const routeQuery = useRouteQuery();
    const urlCertificateId = routeQuery.get('certificateId');
    const openedForCertificateIdRef = useRef<string | null>(null);

    const items = certificatePortfolioItems
        .map((portfolioItem) => {
            const { title, relatedId, relatedType, metadata } = portfolioItem;

            if (!metadata?.certificateId && !relatedId) {
                return null;
            }

            const isReceivedCertificate =
                relatedType === IContentTypeValue.ReceivedCertificate;

            return {
                ...portfolioItem,
                title: isReceivedCertificate
                    ? metadata?.certificateName
                    : title,
            };
        })
        .filter(Boolean)
        .sort((a, b) => {
            if (!a.title || !b.title) {
                return 0;
            }

            if (a.title < b.title) {
                return -1;
            }

            if (a.title > b.title) {
                return 1;
            }

            return 0;
        });

    const setCertificateIdInUrl = useCallback(
        (id: string | null, replace: boolean = false) => {
            const params = new URLSearchParams(location.search);

            if (id) {
                params.set('certificateId', id);
            } else {
                params.delete('certificateId');
            }

            const search = params.toString();

            navigate(
                `${location.pathname}${search ? `?${search}` : ''}${location.hash}`,
                { replace }
            );
        },
        [navigate, location.pathname, location.search, location.hash]
    );

    const hasMandatoryItems = items.some(
        (item) => !!item.metadata?.isMandatory
    );

    const getCertificateIdForItem = (item: IPortfolioItemChildFragment) => {
        const isReceivedCertificate =
            item.relatedType === IContentTypeValue.ReceivedCertificate;

        return isReceivedCertificate
            ? item.metadata?.certificateId
            : item.relatedId;
    };

    const handlePortfolioItemChildrenRefetch = useCallback(() => {
        client.cache.evict({
            id: client.cache.identify({
                __typename: 'PortfolioItem',
                id: portfolioItemId,
            }),
            fieldName: 'children',
        });

        client.cache.gc();
    }, [client, portfolioItemId]);

    useEffect(() => {
        // URL lost its certificateId (e.g. browser back) — close the drawer we opened
        if (!urlCertificateId && openedForCertificateIdRef.current) {
            closeGlobalDrawer({
                type: IGlobalDrawerType.ReceivedCertificate,
            });
            openedForCertificateIdRef.current = null;

            return;
        }

        if (!urlCertificateId || !user) return;
        if (openedForCertificateIdRef.current === urlCertificateId) return;

        const matchingItem = items.find(
            (item) => getCertificateIdForItem(item) === urlCertificateId
        );

        if (!matchingItem) return;

        openedForCertificateIdRef.current = urlCertificateId;

        const { id, title, relatedId, metadata } = matchingItem;
        const isReceivedCertificate =
            matchingItem.relatedType === IContentTypeValue.ReceivedCertificate;

        const handleRefetch = () => {
            handlePortfolioItemChildrenRefetch();
            onUpdate();
        };

        openGlobalDrawer({
            type: IGlobalDrawerType.ReceivedCertificate,
            itemId: id,
            props: {
                title,
                user,
                skill,
                hideUserBar,
                certificateId: isReceivedCertificate
                    ? metadata?.certificateId
                    : relatedId,
                isExternalCertificate: metadata?.isExternalCertificate,
                requiresValidation: metadata?.requiresValidation,
                onClose: () => {
                    setCertificateIdInUrl(null, true);
                },
                onDeleteCertificate: handleRefetch,
                onGenerateCertificate: handleRefetch,
                onSetExternalStatus: handleRefetch,
            },
        });
    }, [
        urlCertificateId,
        items,
        user,
        skill,
        hideUserBar,
        openGlobalDrawer,
        closeGlobalDrawer,
        setCertificateIdInUrl,
        handlePortfolioItemChildrenRefetch,
        onUpdate,
    ]);

    if (!items.length) {
        return (
            <Box sx={{ mt: 3.5 }}>
                <Typography sx={{ mb: 0 }}>
                    {translate('report.noSkillsReports')}
                </Typography>
            </Box>
        );
    }

    return (
        <TableContainer>
            <Table>
                <TableBody>
                    <React.Fragment>
                        <TableRow>
                            <TableCell colSpan={3} sx={{ pt: 3 }}>
                                <Typography fontWeight={700} variant="body2">
                                    {translate(
                                        'skillDrawer.receivedCertificateReportTable.title',
                                        { count: items.length }
                                    )}
                                </Typography>
                            </TableCell>
                        </TableRow>

                        {items.map((item) => (
                            <ReceivedCertificatePortfolioItemTableRow
                                hasMandatoryItems={hasMandatoryItems}
                                item={item}
                                key={item.id}
                                onClick={() => {
                                    if (!user) return;

                                    const certificateId =
                                        getCertificateIdForItem(item);

                                    if (!certificateId) return;

                                    setCertificateIdInUrl(certificateId);
                                }}
                            />
                        ))}
                    </React.Fragment>
                </TableBody>
            </Table>
        </TableContainer>
    );
};
